import { Bell } from "lucide-react";
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NotificationPanel } from "./notification-panel";
import { useUnreadCount } from "./use-notifications";

/** The one bell trigger both modes share — placement owns responsive hiding. */
function BellTrigger({ unread, ...props }: { unread: number } & React.ComponentProps<"button">) {
  return (
    <button
      type="button"
      aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
      className="relative inline-flex h-full cursor-pointer items-center px-3 text-foreground"
      {...props}
    >
      <Bell className="h-[22px] w-[22px]" />
      {unread > 0 && (
        <span
          aria-hidden
          className="absolute right-1 top-2 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[11px] font-bold leading-none text-primary-foreground"
        >
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </button>
  );
}

/**
 * Header notification bell. `mode="popover"` is the desktop dropdown (lives in
 * the md+ nav); `mode="sheet"` is the mobile drawer (lives beside the hamburger).
 * Both share the unread badge and the panel body. The feed only fetches once
 * open; the badge count is live via the websocket handler.
 */
export function NotificationBell({ mode }: { mode: "popover" | "sheet" }) {
  const [open, setOpen] = useState(false);
  const unread = useUnreadCount(true);

  if (mode === "sheet") {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger render={<BellTrigger unread={unread} />} />
        <SheetContent side="right" showCloseButton={false} className="w-[88%] max-w-sm gap-0 p-0">
          <NotificationPanel open={open} onClose={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={<BellTrigger unread={unread} />} />
      <PopoverContent align="end" sideOffset={0} className="w-[380px] overflow-hidden p-0">
        <NotificationPanel open={open} onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
